import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { GIButton } from 'app-components';
import NumericInput from '@components/common/form/NumericInput';
import { ValidationErrorMessage } from '@modules/members/utils';
import { FlexContainer, FlexRow, StyledDiv, StyledInputContainer } from './constants/media.styled';
import { IMediaFormSchema } from './constants/media.interface';
import useMediaFormHandler from './hooks/useMediaFormHandler';
import useQueryParams from '@hooks/useQueryParams';
import useAppSelector from '@hooks/useAppSelector';
import { environmentSelector } from '@selectors/commonSelectors';

interface IMediaFormProps {
    setContentData: React.Dispatch<React.SetStateAction<Object>>;
}

const MediaForm = ({ setContentData }: IMediaFormProps) => {
    const intl = useIntl();
    const queryParams = useQueryParams();
    const contactId = queryParams.get('contactId');
    const { exchangeName } = useAppSelector(environmentSelector);
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
        onSubmitHandler,
        mediaNotFoundError,
        setMediaNotFoundError,
    } = useMediaFormHandler({ defaultValues: contactId ? { interactionId: Number(contactId) } : undefined });
    const values = watch();

    const onSubmit = async (data: IMediaFormSchema) => {
        const { interactionId } = data;
        await onSubmitHandler({ setContentData, contactId: String(interactionId) });
    };

    const handleFieldChange = (value: number | string) => {
        mediaNotFoundError && setMediaNotFoundError('');
        setValue('interactionId', typeof value === 'number' ? value : Number(value));
    };

    const handleCustomPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text');
        if (!isNaN(Number(pastedData))) {
            setValue('interactionId', Number(pastedData));
        }
    };

    useEffect(() => {
        if (contactId) {
            onSubmitHandler({ setContentData, contactId, fromAccountActivity: true });
        }
    }, [contactId]);

    return (
        <FlexContainer data-testid="media-form">
            <h3 data-testid="interaction-header">
                {intl.formatMessage({ id: 'cap.mediaAccess.interactionId.header' })}
            </h3>
            <form onSubmit={handleSubmit(onSubmit)} id="mediaAccessForm" data-testid="media-access-form">
                <FlexContainer className='align-items-center'>
                    <StyledInputContainer data-testid="interaction-id-input-container">
                        <StyledDiv style={{ width: '50%' }}>
                            <FlexRow>
                                <NumericInput
                                    label={{ labelText: '', wrapperClasses: ['m-0', 'text-center', 'flex-1'], isRequired: true }}
                                    input={{
                                        id: 'interactionId',
                                        type: 'number',
                                        value: values.interactionId,
                                        placeholder: intl.formatMessage({
                                            id: 'cap.mediaAccess.placeholder.interactionId',
                                        }),
                                        ...register('interactionId'),
                                        min: 0,
                                        max: undefined,
                                        isInputRequired: true,
                                        inputClasses: ['w-100'],
                                    }}
                                    actions={{
                                        handleFieldChange,
                                        handleCustomPaste,
                                    }}
                                    additionalInvalidKeys={['.', '-']}
                                />
                                <GIButton type='submit' id="goButton" onClickHandler={handleSubmit(onSubmit)} variant='primary' labelId="cap.mediaAccess.button.go" />
                            </FlexRow>
                            <ValidationErrorMessage
                                isVisible={mediaNotFoundError || !!errors.interactionId}
                                messageText={mediaNotFoundError || errors?.interactionId?.message}
                                textReplacements={{ exchangeName }}
                            />
                        </StyledDiv>
                    </StyledInputContainer>
                </FlexContainer>
            </form>
        </FlexContainer>
    );
};

export default MediaForm;